import * as analytics from "@lib/analytics";
import { DEFAULT_SEO } from "@lib/constants";
import * as posthog from "@lib/posthog";
import { usePageProgress } from "@ui/hooks/page-progress";
import type { AppProps } from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import { DefaultSeo } from "next-seo";
import { ThemeProvider } from "next-themes";
import { NuqsAdapter } from "nuqs/adapters/next/pages";
import { PostHogProvider } from "posthog-js/react";
import { useEffect } from "react";
import { Service } from "@/api/common";
import { AdTags } from "@/components/ad-tags";
import { AppErrorBoundary } from "@/components/ui/error-boundary";
import { Toaster } from "@/components/ui/sonner";
import { notificationService } from "@/lib/notifications";

import "emoji-mart/css/emoji-mart.css";
import "@styles/globals.css";
import { NotificationsProvider } from "@/context/notifications";
import { VoiceProvider } from "@/modules/voice";

function App({ Component, pageProps }: AppProps) {
  usePageProgress();
  const router = useRouter();

  useEffect(() => {
    analytics.init();
    notificationService.init();
  }, []);

  // The PostHog client is created with `capture_pageview: false` (the App
  // Router marketing tree fires its own $pageview), so the product app
  // captures pageviews manually: once on load, then on every client-side
  // navigation. Without this, per-screen adoption is invisible in PostHog.
  useEffect(() => {
    const capturePageview = () => analytics.track("$pageview");
    capturePageview();
    router.events.on("routeChangeComplete", capturePageview);
    return () => router.events.off("routeChangeComplete", capturePageview);
  }, [router.events]);

  useEffect(() => {
    const $api = new Service<any>("");
    Object.assign(window, { $api });
  }, []);

  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <Head>
        <link rel="icon" href="/favicon.png" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Head>
      <DefaultSeo {...DEFAULT_SEO} />
      {/* Onboarding is the end of the signup funnel, where subscriptions convert. */}
      {router.pathname.startsWith("/onboarding") && <AdTags />}
      <div>
        <NuqsAdapter>
          <PostHogProvider client={posthog.client}>
            <NotificationsProvider>
              {/*
               * VoiceProvider lives above the page component so active
               * calls survive route changes. It is inert until the user is
               * authenticated and the web-calling feature flag is on.
               */}
              <VoiceProvider>
                {/*
                 * Top-level safety net. Any render error not caught by a more
                 * specific page / section boundary lands here so the user
                 * sees a friendly fallback instead of a blank document.
                 * `resetKey={router.asPath}` clears the fallback as soon as
                 * the route changes, so navigating away always recovers.
                 */}
                <AppErrorBoundary tone="page" scopeLabel="Helios" resetKey={router.asPath}>
                  <Component {...pageProps} />
                </AppErrorBoundary>
              </VoiceProvider>
            </NotificationsProvider>
          </PostHogProvider>
        </NuqsAdapter>
      </div>
      <Toaster />
    </ThemeProvider>
  );
}

export default App;
